import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Random from 'expo-random';
import { decode as decodeBase64ArrayBuffer } from 'base64-arraybuffer';
import { MMKV } from 'react-native-mmkv';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { v4 as uuidv4 } from 'uuid';

import { APP_CONSTANTS } from '@/config/constants';
import {
  IS_CHECKLIST_RECOVERY_DIAGNOSTIC,
  RECOVERY_DIAGNOSTICS_PUBLIC_KEY,
  RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID,
} from '@/config/recoveryDiagnostic';
import { databaseService } from '@/services/databaseService';
import { uploadRecoveryDiagnosticObject } from '@/services/supabase';
import globalState from '@/state/globalState';
import { encryptRecoveryJson } from './checklistRecoveryCrypto';
import { filterRecoveryRows, type RecoveryRow } from './checklistRecoveryScope';

const RECOVERY_FILE_PATTERN =
  /^[0-9]{8}T[0-9]{6}Z-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.agrec$/i;
const MAX_RECOVERY_FILE_BYTES = 25 * 1024 * 1024;
const RECOVERY_DIRECTORY_NAME = 'recovery-diagnostics';
const LAST_SYNC_TIMESTAMP_KEY = 'lastSyncTimestamp';

type GenericRow = RecoveryRow;

export interface RecoveryExportSummary {
  checklistCount: number;
  valueCount: number;
  orphanValueCount: number;
  valueWithoutFieldReferenceCount: number;
  pendingOperationCount: number;
  pendingImageCount: number;
  pendingDeletionCount: number;
  localMediaCount: number;
  skippedLocalMediaCount: number;
}

export interface PreparedRecoveryExport {
  fileUri: string;
  fileName: string;
  objectPath: string;
  identifier: string;
  summary: RecoveryExportSummary | null;
}

export interface RecoveryUploadReceipt {
  identifier: string;
  objectPath: string;
  duplicate: boolean;
  summary: RecoveryExportSummary | null;
}

let recoveryOperationInProgress: Promise<RecoveryUploadReceipt> | null = null;

const readAllRecoveryRows = async (userId: string, companyId: string) => {
  const tables = APP_CONSTANTS.DATABASE.TABLES;
  const [
    users,
    companies,
    templates,
    fields,
    dropdown,
    checklists,
    values,
    markingImages,
    pendingOperations,
    pendingImages,
    pendingDeletions,
  ] = await Promise.all([
    databaseService.listItems<GenericRow>(tables.USERS.NAME),
    databaseService.listItems<GenericRow>(tables.EMPRESAS.NAME),
    databaseService.listItems<GenericRow>(tables.TEMPLATES_CHECKLIST.NAME),
    databaseService.listItems<GenericRow>(tables.GUINCHO_CAMPOS_TEMPLATE.NAME),
    databaseService.listItems<GenericRow>(tables.GUINCHO_DROPDOWN.NAME),
    databaseService.listItems<GenericRow>(tables.GUINCHO_CHECKLISTS.NAME),
    databaseService.listItems<GenericRow>(tables.GUINCHO_VALORES_CHECKLIST.NAME),
    databaseService.listItems<GenericRow>(tables.GUINCHO_MARCACAO_IMAGENS.NAME),
    databaseService.getPendingOperations() as unknown as Promise<GenericRow[]>,
    databaseService.listItems<GenericRow>(tables.PENDING_IMAGES.NAME),
    databaseService.listItems<GenericRow>(tables.PENDING_DELETIONS.NAME),
  ]);

  const rows = filterRecoveryRows({
    userId,
    companyId,
    users,
    companies,
    templates,
    fields,
    checklists,
    values,
    markingImages,
    pendingOperations,
    pendingImages,
    pendingDeletions,
  });

  return {
    rows,
    dropdown,
    rawCounts: {
      users: users.length,
      companies: companies.length,
      templates: templates.length,
      fields: fields.length,
      dropdown: dropdown.length,
      checklists: checklists.length,
      values: values.length,
      markingImages: markingImages.length,
      pendingOperations: pendingOperations.length,
      pendingImages: pendingImages.length,
      pendingDeletions: pendingDeletions.length,
    },
  };
};

const collectLocalMediaMetadata = async (
  values: GenericRow[],
  pendingImages: GenericRow[]
): Promise<{ files: GenericRow[]; skipped: GenericRow[] }> => {
  const candidates = new Set<string>();

  for (const row of values) {
    const value = typeof row.valor === 'string' ? row.valor : '';
    if (value.startsWith('file://') || value.startsWith('content://')) candidates.add(value);
  }
  for (const row of pendingImages) {
    const value = typeof row.local_uri === 'string' ? row.local_uri : '';
    if (value.startsWith('file://') || value.startsWith('content://')) candidates.add(value);
  }

  const files: GenericRow[] = [];
  const skipped: GenericRow[] = [];

  for (const uri of candidates) {
    try {
      const info = await FileSystem.getInfoAsync(uri);
      const size = info.exists && typeof info.size === 'number' ? info.size : 0;
      if (!info.exists) {
        skipped.push({ uri, reason: 'not_found' });
        continue;
      }
      files.push({ uri, bytes: size });
    } catch {
      skipped.push({ uri, reason: 'metadata_error' });
    }
  }

  return { files, skipped };
};

const assertRecoveryDiagnostic = (): void => {
  if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC || Platform.OS !== 'android') {
    throw new Error('Exportacao de recuperacao indisponivel nesta versao.');
  }
};

const getRecoveryDirectoryUri = (userId: string): string => {
  if (!FileSystem.documentDirectory) {
    throw new Error('Diretorio persistente indisponivel.');
  }
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) {
    throw new Error('Usuario local invalido para recuperacao.');
  }
  return `${FileSystem.documentDirectory}${RECOVERY_DIRECTORY_NAME}/${userId}/`;
};

const ensureRecoveryDirectory = async (userId: string): Promise<string> => {
  const directoryUri = getRecoveryDirectoryUri(userId);
  await FileSystem.makeDirectoryAsync(directoryUri, { intermediates: true });
  return directoryUri;
};

const getLocalRecoveryIdentity = async (): Promise<{ userId: string; companyId: string }> => {
  const userId = globalState.user.user.get()?.id;
  if (!userId) {
    throw new Error('Usuario local nao identificado.');
  }

  const localUser = await databaseService.readItem<GenericRow>(
    APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
    userId
  );
  if (!localUser || localUser.id !== userId) {
    throw new Error('Usuario local nao encontrado no banco preservado.');
  }
  const companyId = typeof localUser.empresa_id === 'string' ? localUser.empresa_id : '';
  if (!companyId) {
    throw new Error('Usuario ou empresa local nao identificado.');
  }
  const profileCompanyId = globalState.user.profile.get()?.empresa_id;
  const loadedCompanyId = globalState.company.data.get()?.id;
  if (
    (profileCompanyId && profileCompanyId !== companyId) ||
    (loadedCompanyId && loadedCompanyId !== companyId)
  ) {
    throw new Error('Identidade local divergente. Nao gere o diagnostico neste estado.');
  }
  return { userId, companyId };
};

const getIdentifierFromFileName = (fileName: string): string => {
  const match = fileName.match(
    /^[0-9]{8}T[0-9]{6}Z-([0-9a-f]{8})-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.agrec$/i
  );
  if (!match) {
    throw new Error('Nome do pacote de recuperacao invalido.');
  }
  return match[1].toUpperCase();
};

const toRecoveryFileTimestamp = (isoTimestamp: string): string =>
  isoTimestamp.replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

export const findPendingChecklistRecoveryExport = async (): Promise<PreparedRecoveryExport | null> => {
  assertRecoveryDiagnostic();
  const { userId } = await getLocalRecoveryIdentity();
  const directoryUri = await ensureRecoveryDirectory(userId);
  const entries = await FileSystem.readDirectoryAsync(directoryUri);
  const candidates = entries.filter(name => RECOVERY_FILE_PATTERN.test(name)).sort();
  const fileName = candidates[candidates.length - 1];
  if (!fileName) return null;

  return {
    fileUri: `${directoryUri}${fileName}`,
    fileName,
    objectPath: `${userId}/${fileName}`,
    identifier: getIdentifierFromFileName(fileName),
    summary: null,
  };
};

export const prepareChecklistRecoveryExport = async (): Promise<PreparedRecoveryExport> => {
  assertRecoveryDiagnostic();
  const { userId, companyId } = await getLocalRecoveryIdentity();
  const directoryUri = await ensureRecoveryDirectory(userId);
  console.info('[RECOVERY] prepare:start');
  const snapshot = await readAllRecoveryRows(userId, companyId);
  const { rows } = snapshot;
  const media = await collectLocalMediaMetadata(rows.values, rows.pendingImages);
  const generatedAt = new Date().toISOString();
  const syncStorage = new MMKV();
  const lastSyncTimestamp = syncStorage.getString(LAST_SYNC_TIMESTAMP_KEY) ?? null;
  const checklist299Rows = rows.checklists.filter(
    row => Number(row.sequencial_numero) === 299
  );
  const checklist299Ids = new Set(
    checklist299Rows
      .map(row => typeof row.id === 'string' ? row.id : '')
      .filter(Boolean)
  );
  const checklist299Values = rows.values.filter(
    row => typeof row.checklist_id === 'string' && checklist299Ids.has(row.checklist_id)
  );
  const checklist299PendingOperations = rows.pendingOperations.filter(row => {
    const itemId = typeof row.itemId === 'string'
      ? row.itemId
      : typeof row.item_id === 'string'
        ? row.item_id
        : '';
    let data: Record<string, unknown> | null = null;
    if (row.data && typeof row.data === 'object') {
      data = row.data as Record<string, unknown>;
    } else if (typeof row.data === 'string') {
      try {
        const parsed = JSON.parse(row.data) as unknown;
        data = parsed && typeof parsed === 'object'
          ? parsed as Record<string, unknown>
          : null;
      } catch {
        data = null;
      }
    }
    const dataChecklistId = typeof data?.checklist_id === 'string' ? data.checklist_id : '';
    return checklist299Ids.has(itemId) || checklist299Ids.has(dataChecklistId);
  });
  const payload = {
    schema: 'app-guincho-checklist-recovery-payload/v1',
    generatedAt,
    appVersion: APP_CONSTANTS.VERSION,
    platform: Platform.OS,
    scope: { userId, companyId },
    lastSyncTimestamp,
    rawTableCounts: snapshot.rawCounts,
    rows: {
      ...rows,
      dropdown: snapshot.dropdown,
    },
    checklist299Diagnostic: {
      checklistRows: checklist299Rows,
      values: checklist299Values,
      pendingOperations: checklist299PendingOperations,
      checklistCount: checklist299Rows.length,
      valueCount: checklist299Values.length,
      nonEmptyValueCount: checklist299Values.filter(row => {
        const value = row.valor;
        return value !== null && value !== undefined && String(value).trim() !== '';
      }).length,
      valuesWithValidLocalField: checklist299Values.filter(row =>
        typeof row.campo_id === 'string' && rows.fields.some(field => field.id === row.campo_id)
      ).length,
    },
    localMediaMetadata: media,
  };

  const recipientPublicKey = naclUtil.decodeBase64(RECOVERY_DIAGNOSTICS_PUBLIC_KEY);
  if (recipientPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error('Chave publica de recuperacao invalida nesta versao.');
  }
  const ephemeralSecretKey = await Random.getRandomBytesAsync(nacl.box.secretKeyLength);
  const nonce = await Random.getRandomBytesAsync(24);
  const encrypted = encryptRecoveryJson(
    payload,
    recipientPublicKey,
    ephemeralSecretKey,
    nonce,
    RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID
  );
  const fileId = uuidv4();
  const fileName = `${toRecoveryFileTimestamp(generatedAt)}-${fileId}.agrec`;
  const fileUri = `${directoryUri}${fileName}`;
  const temporaryFileUri = `${fileUri}.tmp`;

  const summary: RecoveryExportSummary = {
    checklistCount: rows.checklists.length,
    valueCount: rows.values.length,
    orphanValueCount: rows.diagnostics.orphanValueIds.length,
    valueWithoutFieldReferenceCount: rows.diagnostics.valuesWithoutFieldReferenceIds.length,
    pendingOperationCount: rows.pendingOperations.length,
    pendingImageCount: rows.pendingImages.length,
    pendingDeletionCount: rows.pendingDeletions.length,
    localMediaCount: media.files.length,
    skippedLocalMediaCount: media.skipped.length,
  };

  await FileSystem.writeAsStringAsync(temporaryFileUri, JSON.stringify(encrypted.encrypted), {
    encoding: FileSystem.EncodingType.UTF8,
  });
  await FileSystem.moveAsync({ from: temporaryFileUri, to: fileUri });
  console.info(
    `[RECOVERY] prepare:ready checklists=${summary.checklistCount} values=${summary.valueCount} orphan_values=${summary.orphanValueCount} missing_field_ref=${summary.valueWithoutFieldReferenceCount} pending=${summary.pendingOperationCount + summary.pendingImageCount + summary.pendingDeletionCount} media_metadata=${summary.localMediaCount} missing=${summary.skippedLocalMediaCount}`
  );

  return {
    fileUri,
    fileName,
    objectPath: `${userId}/${fileName}`,
    identifier: fileId.slice(0, 8).toUpperCase(),
    summary,
  };
};

export const uploadPreparedChecklistRecoveryExport = async (
  prepared: PreparedRecoveryExport
): Promise<RecoveryUploadReceipt> => {
  assertRecoveryDiagnostic();
  const { userId } = await getLocalRecoveryIdentity();
  const expectedDirectoryUri = getRecoveryDirectoryUri(userId);
  const expectedFileUri = `${expectedDirectoryUri}${prepared.fileName}`;
  const expectedObjectPath = `${userId}/${prepared.fileName}`;
  const expectedIdentifier = getIdentifierFromFileName(prepared.fileName);
  if (
    prepared.fileUri !== expectedFileUri ||
    prepared.objectPath !== expectedObjectPath ||
    prepared.identifier !== expectedIdentifier ||
    !RECOVERY_FILE_PATTERN.test(prepared.fileName)
  ) {
    throw new Error('Pacote de recuperacao invalido.');
  }

  const info = await FileSystem.getInfoAsync(prepared.fileUri);
  if (!info.exists || typeof info.size !== 'number' || info.size <= 0) {
    throw new Error('Pacote local de recuperacao ausente.');
  }
  if (info.size > MAX_RECOVERY_FILE_BYTES) {
    throw new Error('Pacote de recuperacao excede o limite seguro de 25 MB.');
  }

  const base64 = await FileSystem.readAsStringAsync(prepared.fileUri, {
    encoding: FileSystem.EncodingType.Base64,
  });
  console.info(`[RECOVERY] upload:start id=${prepared.identifier} bytes=${info.size}`);
  const uploaded = await uploadRecoveryDiagnosticObject(
    prepared.objectPath,
    decodeBase64ArrayBuffer(base64)
  );
  await FileSystem.moveAsync({
    from: prepared.fileUri,
    to: `${prepared.fileUri}.sent`,
  }).catch(() => undefined);
  console.info(`[RECOVERY] upload:success id=${prepared.identifier} duplicate=${uploaded.duplicate}`);
  return {
    identifier: prepared.identifier,
    objectPath: uploaded.path,
    duplicate: uploaded.duplicate,
    summary: prepared.summary,
  };
};

export const generateAndUploadChecklistRecoveryDiagnostic = async (): Promise<RecoveryUploadReceipt> => {
  assertRecoveryDiagnostic();
  if (recoveryOperationInProgress) return recoveryOperationInProgress;

  recoveryOperationInProgress = (async () => {
    const prepared = (await findPendingChecklistRecoveryExport()) ??
      (await prepareChecklistRecoveryExport());
    return uploadPreparedChecklistRecoveryExport(prepared);
  })();

  try {
    return await recoveryOperationInProgress;
  } finally {
    recoveryOperationInProgress = null;
  }
};
